/**
 * AvanceGastarAcumulado
 * Pantalla que muestra el avance por gastar acumulado de todos los proyectos o proyectos seleccionados
 */

import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { proyectoService, imputacionCostoService } from '../services/onebaseService';
import Select from 'react-select';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell } from 'recharts';
import { FiX } from 'react-icons/fi';
import './AvanceGastarAcumulado.css';

import Loading from '../components/Loading';
const AvanceGastarAcumulado = () => {
  const [loading, setLoading] = useState(false);
  const [resumenEjecucion, setResumenEjecucion] = useState(null);
  const [itemsPorEstado, setItemsPorEstado] = useState(null);
  const [proyectos, setProyectos] = useState([]);
  const [proyectosSeleccionados, setProyectosSeleccionados] = useState([]);
  const [presupuestoCostoIds, setPresupuestoCostoIds] = useState([]);
  const [detalleCostosGastados, setDetalleCostosGastados] = useState([]);
  const [showModalDetalleCostos, setShowModalDetalleCostos] = useState(false);

  useEffect(() => {
    cargarProyectos();
    cargarAvanceAcumulado();
  }, []);

  const cargarProyectos = async () => {
    try {
      // Solo cargar proyectos en ejecución
      const response = await proyectoService.getAll({
        status: 'activo',
        estado: 'en_ejecucion'
      });
      const proyectosData = Array.isArray(response.data)
        ? response.data
        : (response.data.results || []);
      setProyectos(proyectosData);
    } catch (error) {
      console.error('Error al cargar proyectos:', error);
      toast.error('Error al cargar proyectos');
    }
  };

  const cargarAvanceAcumulado = async (proyectoIds = []) => {
    setLoading(true);
    try {
      const response = await proyectoService.getAvanceAcumulado(proyectoIds);
      console.log('📊 Respuesta Avance Acumulado:', response.data);
      console.log('📋 Presupuesto Costo IDs:', response.data.resumen.presupuesto_costo_ids);
      setResumenEjecucion(response.data.resumen);
      setItemsPorEstado(response.data.items_por_estado);
      setPresupuestoCostoIds(response.data.resumen.presupuesto_costo_ids || []);
    } catch (error) {
      console.error('Error al cargar avance acumulado:', error);
      toast.error('Error al cargar información de ejecución');
      setResumenEjecucion(null);
      setItemsPorEstado(null);
      setPresupuestoCostoIds([]);
    } finally {
      setLoading(false);
    }
  };

  const handleProyectosChange = (selectedOptions) => {
    setProyectosSeleccionados(selectedOptions || []);
    const proyectoIds = selectedOptions ? selectedOptions.map(opt => opt.value) : [];
    cargarAvanceAcumulado(proyectoIds);
  };

  const cargarDetalleCostosGastados = async (ids = null) => {
    const idsToUse = ids || presupuestoCostoIds;
    console.log('🔍 Cargando imputaciones de costo...');
    console.log('📊 IDs recibidos como parámetro:', ids);
    console.log('📊 IDs del estado presupuestoCostoIds:', presupuestoCostoIds);
    console.log('📊 IDs a usar:', idsToUse);
    try {
      const detalles = [];

      // Consultar imputaciones para cada presupuesto de costo
      if (idsToUse && idsToUse.length > 0) {
        console.log(`✅ Encontrados ${idsToUse.length} presupuestos de costo`);

        for (const presupuestoId of idsToUse) {
          try {
            console.log(`🔎 Consultando imputaciones para presupuesto ${presupuestoId}...`);
            // Consultar imputaciones filtradas por presupuesto_costo
            const response = await imputacionCostoService.getAll({
              presupuesto_costo: presupuestoId,
              ordering: 'area__nombre,partida__nombre,recurso__nombre'
            });

            console.log(`📦 Respuesta completa para presupuesto ${presupuestoId}:`, response.data);
            const imputaciones = Array.isArray(response.data) ? response.data : (response.data.results || []);
            console.log(`📦 Imputaciones encontradas para presupuesto ${presupuestoId}:`, imputaciones.length);
            console.log(`📦 Datos de imputaciones:`, imputaciones);

            // Transformar imputaciones al formato esperado por la tabla
            imputaciones.forEach(imp => {
              console.log('🔍 Procesando imputación:', imp);
              detalles.push({
                area: imp.area_nombre || 'Sin área',
                partida: imp.partida_nombre || 'Sin partida',
                recurso: imp.recurso_nombre || 'Sin nombre',
                descripcion: imp.notas || '',
                tipo_documento: imp.tipo_documento,
                numero_documento: imp.numero_documento,
                cantidad: imp.cantidad,
                precio_unitario: imp.precio_unitario,
                total: imp.total,
                fecha_imputacion: imp.fecha_imputacion,
                created_by: imp.created_by_nombre
              });
            });
          } catch (error) {
            console.error(`❌ Error al cargar imputaciones del presupuesto ${presupuestoId}:`, error);
          }
        }
      } else {
        console.warn('⚠️ No hay presupuesto_costo_ids disponibles');
      }

      console.log(`💰 Total de imputaciones encontradas: ${detalles.length}`);
      console.log('📋 Detalles completos:', detalles);
      setDetalleCostosGastados(detalles);
    } catch (error) {
      console.error('❌ Error loading imputaciones de costo:', error);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: 'CLP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value || 0);
  };

  // Opciones para el selector de proyectos
  const proyectosOptions = proyectos.map(proyecto => ({
    value: proyecto.id,
    label: `${proyecto.cliente_nombre} - ${proyecto.nombre}`
  }));

  if (loading && !resumenEjecucion) {
    return (
      <div className="avance-acumulado-page">
        <Loading fullScreen={false} mensaje="Cargando información de ejecución" />
      </div>
    );
  }

  if (!resumenEjecucion) {
    return (
      <div className="avance-acumulado-page">
        <div className="page-header">
          <h1>📊 Avance por Gastar Acumulado</h1>
        </div>
        <div className="empty-state">
          <p>No hay proyectos con presupuesto de venta activo</p>
        </div>
      </div>
    );
  }

  return (
    <div className="avance-acumulado-page">
      {/* Header */}
      <div className="page-header">
        <h1>📊 Avance por Gastar Acumulado</h1>
        <div className="header-info">
          {resumenEjecucion.cantidad_proyectos > 0 && (
            <span className="info-badge">
              {proyectosSeleccionados.length > 0
                ? `${proyectosSeleccionados.length} ${proyectosSeleccionados.length === 1 ? 'proyecto' : 'proyectos'}`
                : `${resumenEjecucion.cantidad_proyectos} ${resumenEjecucion.cantidad_proyectos === 1 ? 'proyecto' : 'proyectos'}`
              }
            </span>
          )}
        </div>
      </div>

      {/* Filtro de Proyectos */}
      <div className="filtro-proyectos">
        <label>Seleccionar Proyectos (opcional):</label>
        <Select
          isMulti
          options={proyectosOptions}
          value={proyectosSeleccionados}
          onChange={handleProyectosChange}
          placeholder="Todos los proyectos"
          className="proyecto-select"
          classNamePrefix="select"
          isClearable
        />
      </div>

      {/* Contenido principal */}
      <div className="avance-por-gastar-container">
        {/* Grid de métricas financieras - Similar al modal */}
        <div className="presupuesto-metricas-grid">
          <div className="metrica-card">
            <div className="metrica-icon">📋</div>
            <div className="metrica-info">
              <div className="metrica-label">Costo Presupuestado</div>
              <div className="metrica-valor">{formatCurrency(resumenEjecucion.costo_presupuestado || 0)}</div>
            </div>
          </div>

          <div className="metrica-card">
            <div className="metrica-icon">💼</div>
            <div className="metrica-info">
              <div className="metrica-label">Costo Esperado</div>
              <div className="metrica-valor">{formatCurrency(resumenEjecucion.costo_esperado || 0)}</div>
            </div>
          </div>

          <div className="metrica-card">
            <div className="metrica-icon">✅</div>
            <div className="metrica-info">
              <div className="metrica-label">Total Ejecutado</div>
              <div className="metrica-valor ejecutado">{formatCurrency(resumenEjecucion.total_ejecutado)}</div>
            </div>
          </div>

          <div className="metrica-card">
            <div className="metrica-icon">💸</div>
            <div className="metrica-info">
              <div className="metrica-label">Total Gastado</div>
              <div className="metrica-valor gastado">{formatCurrency(resumenEjecucion.total_gastado)}</div>
              <div className="metrica-sublabel">
                Presupuesto + Workflows
              </div>
            </div>
          </div>

          <div className="metrica-card highlight">
            <div className="metrica-icon">💰</div>
            <div className="metrica-info">
              <div className="metrica-label">DESVIACIÓN</div>
              <div className={`metrica-valor ${(resumenEjecucion.desviacion || 0) >= 0 ? 'positivo' : 'negativo'}`}>
                {formatCurrency(resumenEjecucion.desviacion || 0)}
              </div>
              <div className="metrica-sublabel">
                <span className={(resumenEjecucion.desviacion || 0) >= 0 ? 'positivo' : 'negativo'}>
                  {resumenEjecucion.total_ejecutado > 0
                    ? (((resumenEjecucion.desviacion || 0) / resumenEjecucion.total_ejecutado) * 100).toFixed(2)
                    : '0.00'}%
                </span>
              </div>
            </div>
          </div>

          <div className="metrica-card">
            <div className="metrica-icon">🔖</div>
            <div className="metrica-info">
              <div className="metrica-label">Por Gastar</div>
              <div className="metrica-valor">{formatCurrency(resumenEjecucion.por_gastar || 0)}</div>
              <div className="metrica-sublabel">
                Recursos disponibles
              </div>
            </div>
          </div>

          <div className="metrica-card">
            <div className="metrica-icon">📈</div>
            <div className="metrica-info">
              <div className="metrica-label">Proyección</div>
              <div className="metrica-valor">{formatCurrency(resumenEjecucion.proyeccion_total || 0)}</div>
              <div className="metrica-sublabel">
                Costo Esperado + Disponible
              </div>
            </div>
          </div>
        </div>

        {/* Gráfico de Barras */}
        <div className="chart-section">

          <ResponsiveContainer width="100%" height={450}>
            <BarChart
              data={[
                {
                  name: 'Costo Presupuestado',
                  valor: parseFloat(resumenEjecucion.costo_presupuestado) || 0,
                  color: '#3b82f6'
                },
                {
                  name: 'Costo Esperado',
                  valor: parseFloat(resumenEjecucion.costo_esperado) || 0,
                  color: '#69B32D'
                },
                {
                  name: 'Total Ejecutado',
                  valor: parseFloat(resumenEjecucion.total_ejecutado) || 0,
                  color: '#10b981'
                },
                {
                  name: 'Total Gastado',
                  valor: parseFloat(resumenEjecucion.total_gastado) || 0,
                  color: '#ef4444'
                },
                {
                  name: 'Desviación',
                  valor: parseFloat(resumenEjecucion.desviacion) || 0,
                  color: (parseFloat(resumenEjecucion.desviacion) || 0) >= 0 ? '#10b981' : '#ef4444'
                },
                {
                  name: 'Por Gastar',
                  valor: parseFloat(resumenEjecucion.por_gastar) || 0,
                  color: '#f59e0b'
                },
                {
                  name: 'Proyección',
                  valor: parseFloat(resumenEjecucion.proyeccion_total) || 0,
                  color: '#06b6d4'
                }
              ]}
              margin={{ top: 20, right: 30, left: 20, bottom: 80 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis
                dataKey="name"
                angle={-45}
                textAnchor="end"
                height={100}
                tick={{ fill: '#6b7280', fontSize: 12 }}
              />
              <YAxis
                tick={{ fill: '#6b7280', fontSize: 12 }}
                tickFormatter={(value) => `$${(value / 1000000).toFixed(1)}M`}
              />
              <Tooltip
                formatter={(value) => formatCurrency(value)}
                contentStyle={{
                  backgroundColor: 'rgba(255, 255, 255, 0.95)',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                }}
              />
              <Bar
                dataKey="valor"
                radius={[8, 8, 0, 0]}
                onClick={async (data) => {
                  if (data && data.name === 'Total Gastado') {
                    const ids = resumenEjecucion?.presupuesto_costo_ids || [];
                    console.log('🎯 Click en Total Gastado, IDs a cargar:', ids);
                    await cargarDetalleCostosGastados(ids);
                    setShowModalDetalleCostos(true);
                  }
                }}
                cursor="pointer"
              >
                {[
                  { color: '#3b82f6' },
                  { color: '#69B32D' },
                  { color: '#10b981' },
                  { color: '#ef4444' },
                  { color: (parseFloat(resumenEjecucion.desviacion) || 0) >= 0 ? '#10b981' : '#ef4444' },
                  { color: '#f59e0b' },
                  { color: '#06b6d4' }
                ].map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Modal Pantalla Completa - Detalle de Costos Gastados */}
      {showModalDetalleCostos && (
        <div
          className="modal-overlay-fullscreen"
          onClick={() => setShowModalDetalleCostos(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.75)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 10000,
            padding: '2rem'
          }}
        >
          <div
            className="modal-fullscreen"
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: 'white',
              borderRadius: '16px',
              width: '95%',
              height: '90%',
              maxWidth: '1600px',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
              overflow: 'hidden'
            }}
          >
            {/* Header del Modal */}
            <div
              style={{
                background: 'linear-gradient(135deg, #102826 0%, #69B32D 100%)',
                padding: '1.5rem 2rem',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                borderTopLeftRadius: '16px',
                borderTopRightRadius: '16px'
              }}
            >
              <h2 style={{
                color: 'white',
                fontSize: '1.5rem',
                fontWeight: '700',
                margin: 0,
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem'
              }}>
                💸 Detalle de Costos Gastados en Presupuesto
              </h2>
              <button
                onClick={() => setShowModalDetalleCostos(false)}
                style={{
                  background: 'rgba(255, 255, 255, 0.2)',
                  border: 'none',
                  color: 'white',
                  fontSize: '1.5rem',
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s',
                  backdropFilter: 'blur(10px)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)';
                  e.currentTarget.style.transform = 'scale(1.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
                  e.currentTarget.style.transform = 'scale(1)';
                }}
              >
                <FiX />
              </button>
            </div>

            {/* Contenido del Modal */}
            <div style={{
              flex: 1,
              overflow: 'auto',
              padding: '2rem',
              backgroundColor: '#f9fafb'
            }}>
              {detalleCostosGastados && detalleCostosGastados.length > 0 ? (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{
                    width: '100%',
                    borderCollapse: 'collapse',
                    backgroundColor: 'white',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                    borderRadius: '12px',
                    overflow: 'hidden'
                  }}>
                    <thead>
                      <tr style={{
                        background: 'linear-gradient(135deg, #102826 0%, #69B32D 100%)',
                        color: 'white'
                      }}>
                        <th style={{ padding: '16px', textAlign: 'left', fontWeight: 600, fontSize: '0.95rem' }}>Documento</th>
                        <th style={{ padding: '16px', textAlign: 'left', fontWeight: 600, fontSize: '0.95rem' }}>Área</th>
                        <th style={{ padding: '16px', textAlign: 'left', fontWeight: 600, fontSize: '0.95rem' }}>Partida</th>
                        <th style={{ padding: '16px', textAlign: 'left', fontWeight: 600, fontSize: '0.95rem' }}>Recurso</th>
                        <th style={{ padding: '16px', textAlign: 'right', fontWeight: 600, fontSize: '0.95rem' }}>Cantidad</th>
                        <th style={{ padding: '16px', textAlign: 'right', fontWeight: 600, fontSize: '0.95rem' }}>Precio Unit.</th>
                        <th style={{ padding: '16px', textAlign: 'right', fontWeight: 600, fontSize: '0.95rem' }}>Total</th>
                        <th style={{ padding: '16px', textAlign: 'left', fontWeight: 600, fontSize: '0.95rem' }}>Fecha</th>
                      </tr>
                    </thead>
                    <tbody>
                      {detalleCostosGastados.map((detalle, index) => (
                        <tr key={index} style={{
                          borderBottom: '1px solid #e5e7eb',
                          transition: 'background-color 0.2s'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                        >
                          <td style={{ padding: '14px', fontSize: '0.9rem' }}>
                            <div style={{ fontWeight: 600, color: '#1f2937' }}>{detalle.tipo_documento}</div>
                            <div style={{ fontSize: '0.85rem', color: '#6b7280', marginTop: '2px' }}>
                              {detalle.numero_documento}
                            </div>
                          </td>
                          <td style={{ padding: '14px', fontSize: '0.9rem', color: '#374151' }}>{detalle.area}</td>
                          <td style={{ padding: '14px', fontSize: '0.9rem', color: '#374151' }}>{detalle.partida}</td>
                          <td style={{ padding: '14px', fontSize: '0.9rem' }}>
                            <div style={{ fontWeight: 600, color: '#1f2937' }}>{detalle.recurso}</div>
                            {detalle.descripcion && (
                              <div style={{ fontSize: '0.85rem', color: '#6b7280', marginTop: '2px' }}>
                                {detalle.descripcion}
                              </div>
                            )}
                          </td>
                          <td style={{ padding: '14px', textAlign: 'right', fontSize: '0.9rem', color: '#374151' }}>
                            {parseFloat(detalle.cantidad).toFixed(2)}
                          </td>
                          <td style={{ padding: '14px', textAlign: 'right', fontSize: '0.9rem', color: '#374151' }}>
                            {formatCurrency(detalle.precio_unitario)}
                          </td>
                          <td style={{
                            padding: '14px',
                            textAlign: 'right',
                            fontSize: '0.95rem',
                            fontWeight: 700,
                            color: '#ef4444'
                          }}>
                            {formatCurrency(detalle.total)}
                          </td>
                          <td style={{ padding: '14px', fontSize: '0.85rem', color: '#6b7280' }}>
                            {new Date(detalle.fecha_imputacion).toLocaleDateString('es-CL')}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr style={{
                        background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
                        fontWeight: 700,
                        borderTop: '3px solid #69B32D'
                      }}>
                        <td colSpan="6" style={{
                          padding: '16px',
                          textAlign: 'right',
                          fontSize: '1.1rem',
                          color: '#1f2937'
                        }}>
                          TOTAL GASTADO:
                        </td>
                        <td style={{
                          padding: '16px',
                          textAlign: 'right',
                          fontSize: '1.2rem',
                          color: '#ef4444'
                        }}>
                          {formatCurrency(detalleCostosGastados.reduce((sum, d) => sum + parseFloat(d.total || 0), 0))}
                        </td>
                        <td></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              ) : (
                <div style={{
                  textAlign: 'center',
                  padding: '3rem',
                  color: '#6b7280',
                  fontSize: '1.1rem'
                }}>
                  No hay costos gastados registrados
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AvanceGastarAcumulado;
